import type { TemplateField } from "@/types/activity";

/**
 * Generate a realistic JSON content example from content_fields definition.
 */
export function generateContentExample(
  fields: TemplateField[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    result[field.name] = fieldExample(field);
  }
  return result;
}

function fieldExample(field: TemplateField): unknown {
  switch (field.type) {
    case "boolean":
      return true;

    case "integer": {
      const min = field.minimum ?? 0;
      const max = field.maximum ?? min + 10;
      return Math.round((min + max) / 2);
    }

    case "select": {
      const first = field.options?.[0];
      return first ? (typeof first === "string" ? first : first.key) : "";
    }

    case "string":
      return stringExample(field);

    case "file":
      return "https://example.com/media/illustration.png";

    case "video":
      return "https://example.com/media/sample-video.mp4";

    case "audio":
      return { url: "https://example.com/audio/sample.mp3", duration_seconds: 45 };

    case "transcript":
      return ["Première phrase du texte", "Deuxième phrase après une pause", "Troisième segment"];

    case "array":
      return arrayExample(field);

    case "score_points":
      return { option_1: 1, option_2: 0 };

    default:
      return `<${field.type}>`;
  }
}

function stringExample(field: TemplateField): string {
  if (field.format === "uri") return "https://example.com/image.png";
  if (field.input === "richtext" || field.input === "textarea")
    return "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";
  if (field.name.includes("title") || field.name.includes("label"))
    return "Titre d'exemple";
  return "Texte d'exemple";
}

function arrayExample(field: TemplateField): unknown[] {
  const count = Math.max(field.minItems ?? 1, 2);
  if (!field.item) {
    if (field.hasTypology) {
      return Array.from({ length: count }, (_, i) => ({
        value: `item_${i + 1}`,
        typology: i === 0 ? "positive" : "negative",
      }));
    }
    return Array.from({ length: count }, (_, i) => `item_${i + 1}`);
  }

  if (field.item.type === "object" && field.item.fields) {
    return Array.from({ length: count }, (_, i) => {
      const obj: Record<string, unknown> = {};
      for (const sf of field.item!.fields) {
        obj[sf.name] = indexedFieldExample(sf, i);
      }
      if (field.hasTypology) {
        obj.typology = i === 0 ? "positive" : "negative";
      }
      if (field.hasCustomItems) {
        obj.is_custom = i === count - 1;
      }
      return obj;
    });
  }

  // simple typed items
  if (field.hasTypology) {
    return Array.from({ length: count }, (_, i) => ({
      value: field.item!.type === "integer" ? i + 1 : `item_${i + 1}`,
      typology: i === 0 ? "positive" : "negative",
    }));
  }
  return Array.from({ length: count }, (_, i) =>
    field.item!.type === "integer" ? i + 1 : `item_${i + 1}`
  );
}

/** Produce indexed example values for array items (e.g. "Option 1", "Option 2") */
function indexedFieldExample(field: TemplateField, index: number): unknown {
  switch (field.type) {
    case "boolean":
      return index === 0;
    case "integer":
      return index + 1;
    case "string": {
      if (field.format === "uri") return `https://example.com/image_${index + 1}.png`;
      if (field.name === "id") return `option_${index + 1}`;
      if (field.name.includes("label") || field.name.includes("text"))
        return `Option ${index + 1}`;
      return `Valeur ${index + 1}`;
    }
    default:
      return `<${field.type}>`;
  }
}
